import type { Category } from "../types";

export type BlockedKind = "script" | "iframe" | "img" | "link";

/**
 * Which guard caught the element. Only "observer" carries a residual risk: those were
 * already in the DOM when we saw them, so the request may have been issued during
 * parsing. The auditor reports exactly this set.
 */
export type BlockedSource = "setter" | "observer" | "tagged";

export interface BlockedItem {
  kind: BlockedKind;
  source: BlockedSource;
  element: Element;
  /** Original URL, withheld until the category is granted. Empty for inline scripts. */
  url: string;
  /** Original `type` attribute of a script, restored on release. */
  scriptType?: string;
  /** Inline script body, held back for tagged inline snippets. */
  inline?: string;
  category: Category;
  /** Document order, so replay preserves original execution order. */
  order: number;
  released: boolean;
}

/** Marks an element as handled so the guards never process it twice. */
export const HANDLED = "__ckHandled";

/** Insertion-ordered store of everything the guards held back. */
export class BlockedItems {
  private readonly items: BlockedItem[] = [];
  private counter = 0;

  add(item: Omit<BlockedItem, "order" | "released">): BlockedItem {
    const stored: BlockedItem = { ...item, order: this.counter++, released: false };
    this.items.push(stored);
    markHandled(item.element);
    return stored;
  }

  /** Pending items for the given categories, in original document order. */
  pendingFor(categories: readonly Category[]): BlockedItem[] {
    return this.items
      .filter((item) => !item.released && categories.includes(item.category))
      .sort((a, b) => a.order - b.order);
  }

  all(): readonly BlockedItem[] {
    return this.items;
  }

  countFor(category: Category): number {
    return this.items.filter((item) => !item.released && item.category === category).length;
  }
}

export function markHandled(element: Element): void {
  (element as Element & { [HANDLED]?: boolean })[HANDLED] = true;
}

export function isHandled(element: Element): boolean {
  return (element as Element & { [HANDLED]?: boolean })[HANDLED] === true;
}
